/**
 * Há quanto tempo, em texto curto: "há 12 min", "há 3 h", "há 2 dias".
 *
 * É uma DURAÇÃO, não uma data — por isso não passa pelo fuso (Regra 13): o
 * carro está na cabine há 3 h em qualquer fuso. Usada no painel da cabine de
 * pintura (F3.5), no painel web e no app do gestor, que precisam dizer o mesmo
 * número para o mesmo carro.
 *
 * Instante no futuro (relógio do aparelho atrasado em relação ao servidor)
 * vira "agora", nunca "há -3 min". Instante inválido devolve `null`: a tela
 * omite, em vez de escrever "há NaN h".
 */
export function formatElapsed(since: string | Date, now: number = Date.now()): string | null {
  const at = since instanceof Date ? since.getTime() : new Date(since).getTime();
  if (!Number.isFinite(at)) return null;
  const minutes = Math.max(0, Math.floor((now - at) / 60_000));
  if (minutes < 1) return 'agora';
  if (minutes < 60) return `há ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `há ${hours} h`;
  const days = Math.floor(hours / 24);
  return days === 1 ? 'há 1 dia' : `há ${days} dias`;
}

/**
 * Duração em texto curto, sem o "há": "40 min", "5 h", "2 dias". Para o
 * "atrasada 5 h" da etapa (F3.7), que conta para a frente a partir da
 * previsão.
 */
export function formatDuration(ms: number): string {
  const minutes = Math.max(0, Math.floor(ms / 60_000));
  if (minutes < 60) return `${Math.max(minutes, 1)} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} h`;
  const days = Math.floor(hours / 24);
  return days === 1 ? '1 dia' : `${days} dias`;
}

const WEEKDAYS = ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado'];

/**
 * O DIA de uma previsão, como se fala no balcão (F3.7): "hoje", "amanhã",
 * "quinta" (até 6 dias à frente) ou "12/10". É o que o cliente lê: "pintura
 * prevista até quinta". Hora não entra — a duração da etapa é estimativa, e
 * "até 16h40" prometeria uma precisão que ninguém tem.
 *
 * `timeZone` é obrigatório na API (Regra 13: o servidor não tem leitor, e o
 * dia é o da oficina); no app e no painel fica de fora, e vale o fuso do
 * aparelho, que é o de quem lê.
 */
export function formatExpectedDay(
  expectedBy: string | Date,
  now: Date = new Date(),
  timeZone?: string,
): string | null {
  const at = expectedBy instanceof Date ? expectedBy : new Date(expectedBy);
  if (!Number.isFinite(at.getTime())) return null;
  const parts = (d: Date) => {
    // Sem fuso (app e painel): o do aparelho, pelos getters locais — sem
    // `formatToParts`, que o Hermes de versões mais velhas não garante.
    if (!timeZone) {
      const pad = (n: number) => String(n).padStart(2, '0');
      const day = pad(d.getDate());
      const month = pad(d.getMonth() + 1);
      return { ymd: `${d.getFullYear()}-${month}-${day}`, day, month };
    }
    const f = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(d);
    const get = (t: string) => f.find((p) => p.type === t)?.value ?? '';
    return { ymd: `${get('year')}-${get('month')}-${get('day')}`, day: get('day'), month: get('month') };
  };
  const target = parts(at);
  const today = parts(now);
  // Diferença em dias de calendário, pela data no fuso — não por 24h corridas.
  const diff = Math.round((Date.parse(`${target.ymd}T00:00:00Z`) - Date.parse(`${today.ymd}T00:00:00Z`)) / 86_400_000);
  if (diff <= 0) return 'hoje';
  if (diff === 1) return 'amanhã';
  if (diff < 7) {
    const weekday = new Date(`${target.ymd}T12:00:00Z`).getUTCDay();
    return WEEKDAYS[weekday] ?? `${target.day}/${target.month}`;
  }
  return `${target.day}/${target.month}`;
}
